/* eslint-disable @typescript-eslint/no-inferrable-types */
import dayjs from "dayjs";
import "dayjs/locale/es";
import { HOSTNAMES, DEFAULT_HOST_VALUE } from "./hostnames";
import { EMPRESAS_AUTH_V2 } from "./authV2Config";
import {
  PERSON_CATALOG,
  DEFAULT_NUMBER_BANK_STATEMENTS,
  STATUS_CATALOG,
  EDAD_MAXIMA_FECHA_NACIMIENTO,
} from "./constantes";
import { getDecisionIngresoConfig } from "./decisionIngresoConfig";
import { BankAccount, PersonaStructure, StatusCatalog } from "@interfaces/response";
import { PersonalData } from "@interfaces/store";
import {
  BankAccountFormItem,
  MatrimonialRegimesData,
  CivilStatus,
  TaxSystemConfigResponse,
  BankInformationItemConfig,
  ReferencesConfigItem,
  FORM_STATUS_ENUM,
} from "@interfaces/index";
import { UUID } from "crypto";
import type { TestContext } from "yup";
import { CLABE_LENGTH, REFERENCE_DEAFUALT_VALUES } from "@helpers/constantes";
import { ZonaCobertura } from "@helpers/zonasCobertura";

/** Fecha de nacimiento más antigua que se acepta: hoy menos `EDAD_MAXIMA_FECHA_NACIMIENTO` años (`minDate`). */
export const getFechaNacimientoMinima = () => dayjs().subtract(EDAD_MAXIMA_FECHA_NACIMIENTO, "years").startOf("day");

/** `true` si la fecha de nacimiento no rebasa la edad máxima; vacía o inválida se deja a las otras reglas. */
export const esFechaNacimientoDentroDeEdadMaxima = (value?: string | null) => {
  if (!value || !dayjs(value).isValid()) return true;
  return !dayjs(value).isBefore(getFechaNacimientoMinima(), "day");
};

dayjs.locale("es");

const meses = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const DEFAULT_INTEREST_RATE = 30;

export const capitalizeFirstLetter = (str: string) => {
  if (str.length === 0) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getInitialsFromName = (name: string) => {
  if (!name) return "";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

export const removeSpecialCharacters = (value: string) => {
  return value.replace(/[^a-zA-ZáéíóúüÁÉÍÓÚÜñÑ\s']/g, "");
};

export const formatCurrency = (amount: number, currencySymbol = "$") => {
  if (!amount) return `${currencySymbol}0.00`;
  const formattedAmount = amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
  return `${currencySymbol}${formattedAmount}`;
};

export const formatPercent = (value: number) => `${value}%`;

export const amountToText = (amount: number, currencySymbol = ""): string => {
  amount = parseFloat(amount.toFixed(2));

  if (amount < 1000) {
    return `${currencySymbol}${amount}`;
  } else if (amount >= 1000 && amount < 1_000_000) {
    const thousands = Math.floor(amount / 1000);
    return `${currencySymbol}${thousands} mil`;
  } else if (amount >= 1_000_000 && amount < 1_000_000_000) {
    const millions = Math.floor(amount / 1_000_000);
    const residual = amount % 1_000_000;
    const thousands = Math.floor(residual / 1000);

    let result = `${currencySymbol}${millions} ${millions === 1 ? "millón" : "millones"}`;
    if (thousands % 1000 !== 0) {
      result += ` ${thousands} mil`;
    }
    return result;
  } else if (amount >= 1_000_000_000) {
    const billions = Math.floor(amount / 1_000_000_000);
    return `${currencySymbol}${billions} mil millones`;
  }

  return `${currencySymbol}${amount}`;
};

export const splitPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length > 10) {
    const country_code = phoneNumber.slice(0, -10);
    const phone_number = phoneNumber.replace(/\D/g, "").slice(-10);
    return { country_code: country_code[0] === "+" ? country_code : `+${country_code}`, phone_number };
  } else {
    return { country_code: "", phone_number: phoneNumber };
  }
};

export const formatTime = (timeToFormat: number) => {
  const minutes = Math.floor(timeToFormat / 60);
  const seconds = timeToFormat % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";

  if (phoneNumber.length < 10) {
    return phoneNumber;
  }

  const part1 = phoneNumber.substring(0, 2);
  const part2 = phoneNumber.substring(2, 6);
  const part3 = phoneNumber.substring(6, 10);

  return `${part1}-${part2}-${part3}`;
};

// TODO: delete or fix
export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const convertSex = (sexString: string) => {
  if (!sexString) return "";
  return sexString.toLowerCase() === "masculino" ? "H" : "M";
};

export const extractRFCFromCurp = (curp: string) => {
  if (!curp || typeof curp !== "string" || curp.length < 10) {
    return "";
  }

  return curp.substring(0, 10);
};

/**
 * Renta mensual de arrendamiento puro, como la hoja "TABLA AMORT AP" (Excel de Mercader):
 * - monto a financiar = valor − enganche (`valor × enganche%`);
 * - tasa del periodo = tasa anual / 365 × días del periodo (30.41667, mes promedio), no tasa anual / 12;
 * - renta sin IVA = `PMT(tasa del periodo, plazo, −monto a financiar, valor residual)`;
 * - renta con IVA = renta × (1 + IVA%).
 * Porcentajes en 0–100 (ej. `tasaAnual: 25`). Para rentas semanales (WuWei): `diasPeriodo: 7` y `plazo` en semanas.
 */
export const calcularRentaArrendamientoPuro = ({
  valor,
  enganchePct,
  plazo,
  tasaAnual,
  residualPct = 0,
  ivaPct = 16,
  diasPeriodo = 30.41667,
}: {
  valor: number;
  enganchePct: number;
  plazo: number;
  tasaAnual: number;
  residualPct?: number;
  ivaPct?: number;
  diasPeriodo?: number;
}) => {
  const montoFinanciar = valor - valor * (enganchePct / 100);
  const residual = valor * (residualPct / 100);
  if (!montoFinanciar || !plazo) return { montoFinanciar, rentaSinIva: 0, rentaConIva: 0 };

  const tasaPeriodo = (tasaAnual / 100 / 365) * diasPeriodo;
  // PMT de Excel con pv = −monto a financiar y fv = valor residual (pago al final del periodo).
  const rentaSinIva =
    tasaPeriodo === 0
      ? (montoFinanciar - residual) / plazo
      : ((montoFinanciar * Math.pow(1 + tasaPeriodo, plazo) - residual) * tasaPeriodo) /
        (Math.pow(1 + tasaPeriodo, plazo) - 1);

  return { montoFinanciar, rentaSinIva, rentaConIva: rentaSinIva * (1 + ivaPct / 100) };
};

export const calculateAmountMonthly = (
  loanAmount: number,
  loanTerm: number, // puede venir en días o en meses
  anualRate: number = DEFAULT_INTEREST_RATE,
  commissionPercent?: number | null,
  ivaPercent?: number | null,
) => {
  const roundTo = (value: number, decimals: number) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };

  // si el plazo es mayor a 60 lo tratamos como días, si no, ya son meses
  const loanTermMonths = loanTerm > 60 ? Math.ceil(loanTerm / 30) : loanTerm;

  const hasCommissionAndIva =
    commissionPercent !== null &&
    commissionPercent !== undefined &&
    commissionPercent > 0 &&
    ivaPercent !== null &&
    ivaPercent !== undefined &&
    ivaPercent > 0;

  // Si NO hay comisión/IVA (o vienen como 0/null), mantenemos el cálculo actual.
  if (!hasCommissionAndIva) {
    const monthlyRate = anualRate / 12 / 100;

    const payment = (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -loanTermMonths));

    return Number(payment.toFixed(2));
  }

  // Modelo ArraCapital (alineado a la tabla):
  // principal = monto + (monto * comisión% * (1 + iva%))
  // pago_capital = principal / plazo
  // interés = principal * tasa_mensual (Arra: 2.7% mensual, provisto como anual en interfaceConfig)
  // iva_interes = interés * iva%
  // pago_total = pago_capital + interés + iva_interes
  const normalizedAnnualRate = anualRate > 1 ? anualRate / 100 : anualRate;
  // `interest_rate` se pasa como tasa anual efectiva; convertimos a mensual efectiva
  // y acotamos precisión para evitar diferencias contra la tabla PDF por decimales ocultos.
  const monthlyRate = roundTo(Math.pow(1 + normalizedAnnualRate, 1 / 12) - 1, 4);

  const commissionRate = commissionPercent > 1 ? commissionPercent / 100 : commissionPercent;
  const ivaRate = ivaPercent > 1 ? ivaPercent / 100 : ivaPercent;

  const commission = roundTo(loanAmount * commissionRate * (1 + ivaRate), 2);
  const principal = roundTo(loanAmount + commission, 2);
  const paymentCapital = roundTo(principal / loanTermMonths, 2);
  const interest = roundTo(principal * monthlyRate, 2);
  const ivaInterest = roundTo(interest * ivaRate, 2);
  const paymentTotal = paymentCapital + interest + ivaInterest;

  return Number(paymentTotal.toFixed(2));
};

export const obtenerFechaHoy = (): string => {
  const hoy = new Date();
  const dia = hoy.getDate();
  const mes = meses[hoy.getMonth()];
  const año = hoy.getFullYear();

  return `${dia} de ${mes} de ${año}`;
};

// Formatear la fecha a formato largo, Ej: 01 de enero de 2021
export const formatDateToLargeDate = (str_date: string) => {
  return dayjs(str_date).format(`DD [de] MMMM [de] YYYY`);
};

export const getMimeTypeFromFilename = (filename: string): string => {
  const extension = filename.split(".")[1];

  return (
    {
      aac: "audio/aac",
      abw: "application/x-abiword",
      arc: "application/x-freearc",
      avi: "video/x-msvideo",
      azw: "application/vnd.amazon.ebook",
      bin: "application/octet-stream",
      bmp: "image/bmp",
      bz: "application/x-bzip",
      bz2: "application/x-bzip2",
      cda: "application/x-cdf",
      csh: "application/x-csh",
      css: "text/css",
      csv: "text/csv",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      eot: "application/vnd.ms-fontobject",
      epub: "application/epub+zip",
      gz: "application/gzip",
      gif: "image/gif",
      htm: "text/html",
      html: "text/html",
      ico: "image/vnd.microsoft.icon",
      ics: "text/calendar",
      jar: "application/java-archive",
      jpeg: "image/jpeg",
      jpg: "image/jpeg",
      js: "text/javascript",
      json: "application/json",
      jsonld: "application/ld+json",
      mid: "audio/midi audio/x-midi",
      midi: "audio/midi audio/x-midi",
      mjs: "text/javascript",
      mp3: "audio/mpeg",
      mp4: "video/mp4",
      mpeg: "video/mpeg",
      mpkg: "application/vnd.apple.installer+xml",
      odp: "application/vnd.oasis.opendocument.presentation",
      ods: "application/vnd.oasis.opendocument.spreadsheet",
      odt: "application/vnd.oasis.opendocument.text",
      oga: "audio/ogg",
      ogv: "video/ogg",
      ogx: "application/ogg",
      opus: "audio/opus",
      otf: "font/otf",
      png: "image/png",
      pdf: "application/pdf",
      php: "application/x-httpd-php",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      rar: "application/vnd.rar",
      rtf: "application/rtf",
      sh: "application/x-sh",
      svg: "image/svg+xml",
      swf: "application/x-shockwave-flash",
      tar: "application/x-tar",
      tif: "image/tiff",
      tiff: "image/tiff",
      ts: "video/mp2t",
      ttf: "font/ttf",
      txt: "text/plain",
      vsd: "application/vnd.visio",
      wav: "audio/wav",
      weba: "audio/webm",
      webm: "video/webm",
      webp: "image/webp",
      woff: "font/woff",
      woff2: "font/woff2",
      xhtml: "application/xhtml+xml",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      xml: "application/xml",
      xul: "application/vnd.mozilla.xul+xml",
      zip: "application/zip",
      "3gp": "video/3gpp",
      "3g2": "video/3gpp2",
      "7z": "application/x-7z-compressed",
    }[extension] || "application/octet-stream"
  );
};

export const forceDownload = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getMonthName = (monthNumber: number) => {
  return meses[monthNumber - 1] || "";
};

export const transformTitleCase = (value: string) => {
  if (!value) return value;
  return removeSpecialCharacters(value);
};

export const getOrdinalText = (index: number) => {
  const ordinals = [
    "",
    "Primera",
    "Segunda",
    "Tercera",
    "Cuarta",
    "Quinta",
    "Sexta",
    "Séptima",
    "Octava",
    "Novena",
    "Décima",
  ];
  return ordinals[index] || "";
};

export const getShortOrdinal = (n: number): string => {
  const last_two = n % 100;
  const last_one = n % 10;

  let sufijo: string;

  if ([11, 12, 13].includes(last_two)) {
    sufijo = "mo";
  } else {
    switch (last_one) {
      case 1:
        sufijo = "er";
        break;
      case 2:
        sufijo = "do";
        break;
      case 3:
        sufijo = "er";
        break;
      case 4:
      case 5:
      case 6:
        sufijo = "to";
        break;
      case 7:
        sufijo = "mo";
        break;
      case 8:
        sufijo = "vo";
        break;
      case 9:
        sufijo = "no";
        break;
      case 0:
        sufijo = "mo";
        break;
      default:
        sufijo = "";
    }
  }

  return `${n}${sufijo}`;
};

export const separarNombres = (nombreCompleto: string) => {
  const partes = nombreCompleto.split(" ");

  let nombre = "";
  let apellido1 = "";
  let apellido2 = "";

  if (partes.length >= 2) {
    nombre = partes.slice(0, partes.length - 2).join(" ");
    apellido1 = partes[partes.length - 2];
    apellido2 = partes[partes.length - 1];
  } else if (partes.length === 1) {
    nombre = partes[0];
  }

  return {
    nombre,
    apellido1,
    apellido2,
  };
};

export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength);
};

const images = import.meta.glob("/src/assets/img/**", { eager: true });

export const getDynamicUrl = (fileName: string): string | undefined => {
  const hostname = getBaseDomain(window.location.href);
  const foundHost = HOSTNAMES.find(({ host }) => host === hostname);
  const folder = foundHost?.value || "finbe";
  const path = `/src/assets/img/${folder}/${fileName}`;

  return (images as Record<string, { default: string }>)[path]?.default;
};

export const getBaseDomain = (url: string): string | null => {
  try {
    if (!/^https?:\/\//.test(url)) {
      url = "http://" + url;
    }

    const hostname = new URL(url).hostname;

    return hostname.replace(/^www\./, "");
  } catch (error) {
    return null;
  }
};

export const getConfigName = (url: string): string => {
  try {
    const hostname = getBaseDomain(url);
    const hostValue = HOSTNAMES.find(({ host }) => host === hostname)?.value;
    return hostValue || DEFAULT_HOST_VALUE;
  } catch (_error) {
    return DEFAULT_HOST_VALUE;
  }
};

export const getProportionalHeight = (img_width: number, img_height: number, new_width: number | string) => {
  const result = (img_height / img_width) * +new_width;
  return result;
};

export const capitalizeWords = (sentence: string) => {
  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

export const identifyPersons = (
  persons: PersonaStructure[],
  list_civil_status?: CivilStatus[],
  list_matrimonial_regimen?: MatrimonialRegimesData[],
) => {
  const representantes: PersonaStructure[] = [];
  const avales: PersonaStructure[] = [];
  const conyuges: PersonaStructure[] = [];
  const propietarios: PersonaStructure[] = [];
  const segundos_avales: PersonaStructure[] = [];

  persons.forEach((personItem) => {
    const person_codes = personItem.person_types_detail.map((item) => item.code);
    if (person_codes.includes(PERSON_CATALOG.representante_legal.code)) {
      representantes.push(personItem);
    }
    if (person_codes.includes(PERSON_CATALOG.accionista_mayoritario.code)) {
      if (personItem.is_shareholder) {
        avales.push(personItem);
      }
    }
    if (person_codes.includes(PERSON_CATALOG.conyuge_aval.code)) {
      conyuges.push(personItem);
    }
    if (person_codes.includes(PERSON_CATALOG.propietario_real.code)) {
      if (personItem.is_shareholder) {
        propietarios.push(personItem);
      }
    }
    if (person_codes.includes(PERSON_CATALOG.segundo_aval.code)) {
      segundos_avales.push(personItem);
    }
  });

  const aval = avales.length > 0 ? avales[0] : undefined;
  let conyuge_aval = undefined;
  let aval_civil_status = undefined;
  let aval_matrimonial_regimen = undefined;

  if (aval) {
    const selected_civil_status = list_civil_status?.find((item) => item.id === aval.civil_status_id);

    aval_civil_status = selected_civil_status?.name.toLowerCase();

    if (aval_civil_status === "casado") {
      conyuge_aval = conyuges[0] || undefined;

      const selected_matrimonial_regimen = list_matrimonial_regimen?.find(
        (item) => item.id === aval.matrimonial_regimes_id,
      );

      if (selected_matrimonial_regimen) {
        aval_matrimonial_regimen = selected_matrimonial_regimen.code;
      }
    }
  }

  const segundos_avales_editable: PersonaStructure[] = segundos_avales.map((item) => ({
    ...item,
    personal_information_status:
      item.personal_information_status === "PENDIENTE" ? "INCIDENCIA" : item.personal_information_status,
    bank_references_status: item.bank_references_status === "PENDIENTE" ? "INCIDENCIA" : item.bank_references_status,
    identity_validation_completed:
      item.identity_validation_completed === null ? false : item.identity_validation_completed,
  }));

  return {
    representante: representantes.length > 0 ? representantes[0] : undefined,
    representantes,
    aval,
    conyuge_aval,
    propietarios,
    segundos_avales: segundos_avales_editable,
    aval_civil_status,
    aval_matrimonial_regimen,
    conyuges,
  };
};

export const formatCompanyList = (companies: string[], useA: boolean = false): string => {
  if (!companies || companies.length === 0) return "";

  const lastConnector = useA ? " y a " : " y ";

  if (companies.length === 1) return companies[0];
  if (companies.length === 2) return companies.join(lastConnector);

  const allExceptLast = companies.slice(0, -1).join(", ");
  const last = companies[companies.length - 1];
  return `${allExceptLast}${lastConnector}${last}`;
};

export const getAmplifyConfigByPath = (path: string): "client" | "guarantee" => {
  if (path.startsWith("/avales")) return "guarantee";
  return "client";
};

export const identifyBankAccounts = ({
  bank_accounts,
  company,
  aval,
}: {
  bank_accounts: BankAccount[];
  company?: PersonalData;
  aval?: PersonaStructure;
}) => {
  const active_accounts = bank_accounts.filter((item) => item.active === true);
  const company_accounts = active_accounts.filter((item) => item.client_details_id === company?.id);
  const aval_accounts = active_accounts.filter((item) => item.person_id === aval?.id);

  return {
    company_accounts,
    aval_accounts,
  };
};

export const createEmptyBankAccount = ({
  number_statements = 3,
}: {
  number_statements: number;
}): BankAccountFormItem => {
  const new_account: BankAccountFormItem = {
    id: "",
    clabe: "",
    bank_id: "",
    bank_name: "",
    statements: Array(number_statements).fill({
      bank_statement_document_id: undefined,
      document_id: undefined,
      document_order: undefined,
      document_value: undefined,
    }),
  };

  return new_account;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormikArrayError = (formik: any, arrayName: string, index: number, inputName: string): string => {
  try {
    const { errors, touched } = formik;

    if (!errors || !touched) {
      return "";
    }

    if (!touched[arrayName] || !touched[arrayName][index] || !touched[arrayName][index][inputName]) {
      return "";
    }

    if (!errors[arrayName]) {
      return "";
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputErrors = errors[arrayName] as any;
    if (!inputErrors || !inputErrors[index] || !inputErrors[index][inputName]) {
      return "";
    }

    return inputErrors[index][inputName] || "";
  } catch (error) {
    return "error_ocurrido " + (error as Error).message;
  }
};

export const getProductBaseRoute = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean);
  return `/${parts[0]}/${parts[1]}/`;
};

export const buildProductRoute = (pathname: string, subRoute: string) => {
  try {
    const parts = (pathname || "").split("/").filter(Boolean);
    const productSegment = parts[1] || "creditosimple";
    return `/producto/${productSegment}/${subRoute}`;
  } catch (_e) {
    return `/producto/creditosimple/${subRoute}`;
  }
};

export const getPersonFullName = (person: PersonaStructure) => {
  return `${person.name || ""} ${person.last_name || ""} ${person.last_name_2 || ""}`.trim();
};

export const substractDateFromRFC = (rfc: string) => {
  const date_part = rfc.substring(4, 10);
  if (date_part.length < 6) {
    return null;
  }
  return dayjs(date_part, "YYMMDD").isValid() ? date_part : null;
};

export const substractDateFromCURP = (curp: string) => {
  const date_part = curp.substring(4, 10);
  if (date_part.length < 6) {
    return null;
  }
  return dayjs(date_part, "YYMMDD").isValid() ? date_part : null;
};

export const validateDates = (date_birth: string, date_part: string | null) => {
  if (!date_part) return false;
  const date_shareholder = dayjs(date_birth, "YYYY-MM-DD").format("YYMMDD");
  return date_shareholder === date_part;
};

interface GenerateUrlPortalProps {
  taxSystemConfig: TaxSystemConfigResponse;
  loan_amount: number;
  loan_term: number;
  motorbike_catalog_id?: string;
}

export const generateUrlPortal = ({
  taxSystemConfig,
  loan_amount,
  loan_term,
  motorbike_catalog_id,
}: GenerateUrlPortalProps) => {
  const { id, product_id, code, tax_system_type } = taxSystemConfig;

  const parts = [
    `tax_system_id=${id}`,
    `product_id=${product_id}`,
    `person_type=${code}`, //tax system code
    `tax_system_type=${tax_system_type}`, // pm, pfae
    `loan_amount=${loan_amount}`,
    `loan_term=${loan_term}`,
  ];

  if (motorbike_catalog_id) {
    parts.push(`motorbike_catalog_id=${motorbike_catalog_id}`);
  }

  return `/producto/creditosimple/crear_cuenta?${parts.join("&")}`;
};

export const getRemainingTime = (last_code_request: string, time_limit: number) => {
  if (!last_code_request) {
    return 0;
  }

  const lastCodeRequest = dayjs(last_code_request);
  const currentTime = dayjs();

  const secondsFromLastRequest = currentTime.diff(lastCodeRequest, "seconds");
  const secondsToWait = time_limit - secondsFromLastRequest;

  return secondsToWait;
};

/**
 * Returns the base URL (protocol + host) in the form "https://my-site.com" or
 * "https://my-site.com.mx". Strips the "www." subdomain when present.
 */
export const getBaseUrl = (url: string) => {
  let normalizedUrl = url.trim();

  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = "https://" + normalizedUrl;
  }

  try {
    const parsed = new URL(normalizedUrl);
    const host = parsed.hostname.toLowerCase().startsWith("www.") ? parsed.hostname.slice(4) : parsed.hostname;
    const port = parsed.port && parsed.port !== "80" && parsed.port !== "443" ? `:${parsed.port}` : "";
    return `${parsed.protocol}//${host}${port}`;
  } catch (e) {
    throw new Error(`URL inválida: ${url}`);
  }
};

export const generateQuotationAmountMarks = (min: number, max: number) => {
  const marks = [];

  if (min < max) {
    marks.push({ value: min, label: amountToText(min) });
    marks.push({ value: max, label: amountToText(max) });
  }

  return marks;
};

export const generateQuotationTermMarks = (min: number, max: number, step: number) => {
  const marks = [];

  if (min < max && step > 0) {
    for (let i = min; i <= max; i += step) {
      marks.push({ value: i, label: `${i}` });
    }
  }

  return marks;
};

export const getNumberOrDefault = (value: unknown, defaultValue: number = 0): number => {
  return typeof value === "number" && !Number.isNaN(value) ? value : defaultValue;
};

export const getBankInformationConfig = (bank_information_config?: BankInformationItemConfig) => {
  const total_items = getNumberOrDefault(bank_information_config?.total_statements) || DEFAULT_NUMBER_BANK_STATEMENTS;
  let required_items = getNumberOrDefault(bank_information_config?.min_statements);

  if (required_items > total_items) {
    required_items = total_items;
  }

  return { total_items, required_items };
};

export const getBankStatementIndex = (path: string) => Number(path.match(/statements\[(\d+)\]/)?.[1] ?? -1);

export function calculatePaymentLessing(tasa: number, nper: number, va: number, vf = 0, tipo = 0): number {
  if (nper <= 0) return NaN;

  // Si tasa = 0, es simplemente (va + vf) / nper con el signo de Excel
  if (tasa === 0) {
    return -(va + vf) / nper;
  }

  const r = tasa;
  const pvif = Math.pow(1 + r, nper);

  // PMT = - r * (va*pvif + vf) / ((pvif - 1) * (1 + r*tipo))
  const pmt = (-r * (va * pvif + vf)) / ((pvif - 1) * (1 + r * tipo));

  return pmt;
}

/* calculates the number of months from the number of weeks, using 4 weeks per month */
export const weeksToMonths = (weeks: number) => {
  if (!Number.isFinite(weeks)) return 0;
  const weeks_in_month = 4;
  return Math.ceil(weeks / weeks_in_month);
};

/**
 * Opens a file in a new tab
 * @param fileUrl - The URL of the file to open
 */
export const openFileInNewTab = (fileUrl: string) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type EmptyReferenceBase = {
  id: UUID;
  country_code: string;
  phone: string;
  phone_extension: string;
  contact_name: string;
  email: string;
  share: number;
};

export function createEmptyReference(partyField?: "cliente"): EmptyReferenceBase & { cliente: string };
export function createEmptyReference(partyField: "proveedor"): EmptyReferenceBase & { proveedor: string };
export function createEmptyReference(partyField: "cliente" | "proveedor" = "cliente") {
  return {
    id: "" as UUID,
    country_code: "+52",
    phone: "",
    phone_extension: "",
    contact_name: "",
    email: "",
    share: 0,
    [partyField]: "",
  };
}

/**
 * Normaliza la configuración de un tipo de referencia: cuántos campos mostrar
 * (`total_items`) y cuántos de esos deben llenarse (`min_items`).
 *
 * Usa `references_config.required` como cantidad de campos y `references_config.min`
 * como mínimo a completar. Si faltan valores o no son números, usa
 * `REFERENCE_DEAFUALT_VALUES`. Si min es mayor que required, `min_items` se
 * recorta para no pasar de `total_items`.
 *
 * @param references_config - Configuración opcional con `min` y `required`
 * @returns `{ total_items, min_items }`
 */
export const getReferenceConfig = (references_config?: ReferencesConfigItem) => {
  const total_items = getNumberOrDefault(references_config?.required) || REFERENCE_DEAFUALT_VALUES.required;
  let min_items = getNumberOrDefault(references_config?.min, REFERENCE_DEAFUALT_VALUES.min);

  if (min_items > total_items) {
    min_items = total_items;
  }

  return { total_items, min_items };
};

export const getReferenceIndex = (path: string) => Number(path.match(/\[(\d+)\]/)?.[1] ?? -1);

export const toNumber = (value: string) => {
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};
export const formatCurrencyInput = (value: string) => {
  const digitsOnly = value.replace(/[^0-9]/g, "");
  return digitsOnly === "" ? "" : Number(digitsOnly).toLocaleString("en-US");
};

export const formatCurrencyFromNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") return "";
  const rounded = Math.round(Number(value));
  return Number.isNaN(rounded) ? "" : rounded.toLocaleString("en-US");
};

/** Pesos sin decimales, con separadores de miles: `$1,200,000`. */
export const formatCurrencyWhole = (amount: number) => `$${Math.round(amount || 0).toLocaleString("en-US")}`;

/**
 * Monto corto en miles o millones: `$150K`, `$3M`, `$2.5M`. Con `withSpace` deja un espacio antes de
 * la unidad (`$150 K`), como lo pide el diseño de algunos cotizadores.
 */
export const formatCurrencyShort = (amount: number, withSpace = false) => {
  const space = withSpace ? " " : "";
  if (amount < 1_000_000) return `$${Math.round(amount / 1000).toLocaleString("es-MX")}${space}K`;
  const millions = amount / 1_000_000;
  return `$${Number.isInteger(millions) ? millions.toLocaleString("es-MX") : millions.toFixed(1)}${space}M`;
};

/**
 * Opciones de plazo de un cotizador: de `minTerm` a `maxTerm` con saltos de `stepTerm` (12–36 cada 6
 * → 12, 18, 24, 30, 36). El máximo siempre se ofrece aunque el salto no caiga exacto en él. Con un
 * rango inválido regresa una lista vacía.
 */
export const buildTermOptions = (minTerm: number, maxTerm: number, stepTerm: number) => {
  if (minTerm <= 0 || maxTerm < minTerm || stepTerm <= 0) return [];
  const terms: number[] = [];
  for (let term = minTerm; term <= maxTerm; term += stepTerm) {
    terms.push(term);
  }
  if (terms[terms.length - 1] !== maxTerm) terms.push(maxTerm);
  return terms;
};

/** El plazo de la lista más cercano a `target` (el default del config puede no caer en un botón). */
export const getClosestTerm = (terms: number[], target: number) =>
  terms.reduce((best, term) => (Math.abs(term - target) < Math.abs(best - target) ? term : best), terms[0] ?? 0);

/**
 * Builds the full URL of a static resource (logos, favicons, background images) from the path
 * stored in the tenant config. Returns an empty string when there is no path, so callers can
 * pass the result straight to `src` without rendering a broken asset.
 * @param path - Resource path relative to the static resources host, e.g. `lendia/logo.svg`
 */
export const getStaticResourceUrl = (path?: string | null) => {
  if (!path?.trim()) return "";
  return `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${path.replace(/^\/+/, "")}`;
};

export const isFormStatusEditable = (status: FORM_STATUS_ENUM) => {
  const actual = status ?? "PENDIENTE";
  return actual === "PENDIENTE" || actual === "INCIDENCIA";
};

/**
 * Status de la vía de ingresos que el usuario NO eligió en `DecisionIngreso`, para que el flujo se los
 * brinque (la secuencia de `status_config` es lineal y trae las dos vías). Regresa `[]` (flujo lineal) si:
 * - todavía no elige vía;
 * - la empresa no tiene `branches` en `decisionIngresoConfig`;
 * - la decisión de su `status_config` no es v2: así la decisión legacy (SFI) no cambia y, al migrarla, se
 *   activa sola en cuanto su URL apunte a `/v2`.
 */
export const getIncomeBranchStatusToDiscard = (
  statusConfig: StatusCatalog[],
  configName: string,
  incomeOption: "sat" | "documents" | null,
  taxSystemType?: string | null,
): string[] => {
  const { branches } = getDecisionIngresoConfig(configName, taxSystemType);
  if (!incomeOption || !branches) return [];

  const decision = statusConfig.find((item) => item.code === STATUS_CATALOG.desicion_sat.code);
  if (!decision?.url.replace(/^\//, "").startsWith("v2/")) return [];

  return incomeOption === "sat" ? branches.documents : branches.sat;
};

/** Siguiente status de `status` en la secuencia de `status_config`, sin los de `discard`. */
export const getNextStatus = (statusConfig: StatusCatalog[], status: string, discard: string[]) => {
  const list = statusConfig.filter((item) => !discard.includes(item.code));
  const index = list.findIndex((item) => item.code === status);
  return index === -1 ? null : (list[index + 1] ?? null);
};

/**
 * Valida el dígito verificador (posición 18) de una CLABE interbancaria.
 *
 * Uso en Formik / Yup:
 * ```ts
 * clabe: yup.string().test("check-digit", "CLABE inválida", (value) => {
 *   if (!value || value.length !== CLABE_LENGTH) return true;
 *   return validateClabeCheckDigit(value);
 * })
 * ```
 * Algoritmo: ponderar los primeros 17 dígitos con pesos [3, 7, 1] repetidos,
 * tomar la unidad de cada producto, sumar; dígito verificador = (10 − suma mod 10) mod 10.
 */
export const validateClabeCheckDigit = (clabe: string): boolean => {
  if (clabe.length !== CLABE_LENGTH) return false;
  const weights = [3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    sum += (parseInt(clabe[i]) * weights[i % 3]) % 10;
  }
  return (10 - (sum % 10)) % 10 === parseInt(clabe[17]);
};

/** Ruta comparable: con una sola `/` al inicio y en minúsculas (`producto/X` → `/producto/x`). */
export const normalizeUrl = (url: string) => `/${url.replace(/^\/+/, "")}`.toLowerCase();

/** Id local para las filas de una lista antes de guardarlas (key de React). */
export const createLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Para enlaces dentro del label de un check: abren sin marcar/desmarcar la casilla. */
export const stopPropagation = (event: { stopPropagation: () => void }) => event.stopPropagation();

/** Test de yup: con `has_social_media` activo, al menos una red social (LinkedIn, Facebook, Instagram o X) capturada. */
export function atLeastOneSocialTest(this: TestContext) {
  const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
  if (has_social_media) {
    return linkedin || facebook || instagram || twitter ? true : false;
  }
  return true;
}

/** Texto en minúsculas, sin acentos y sin espacios de más, para comparar nombres de catálogos. */
export const normalizarTexto = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/**
 * Si el domicilio (estado y municipio del catálogo de CP) está en la zona de cobertura, ej. `ZONA_COBERTURA_WUWEI`:
 * su estado está completo en la zona o su municipio es uno de los de su estado. Compara sin acentos ni mayúsculas, de
 * los dos lados (`normalizarTexto`).
 */
export const estaEnZonaCobertura = (
  { estado, municipio }: { estado: string; municipio: string },
  zona: ZonaCobertura,
) => {
  const igual = (a: string, b: string) => normalizarTexto(a) === normalizarTexto(b);
  if (!estado.trim()) return false;
  if (zona.estados.some((item) => igual(item, estado))) return true;
  const municipios = Object.entries(zona.municipiosPorEstado).find(([nombre]) => igual(nombre, estado))?.[1] ?? [];
  return municipios.some((item) => igual(item, municipio));
};

/** Si la empresa (`getConfigName`) ya usa el acceso v2 (`EMPRESAS_AUTH_V2`). */
export const usaAuthV2 = (configName: string) => EMPRESAS_AUTH_V2.includes(configName);

// ─── CURP y RFC calculados (sin servicio) ─────────────────────────────────────

/** Partículas que no cuentan en nombres y apellidos (RENAPO y SAT). */
const PARTICULAS_NOMBRE = new Set([
  "DA",
  "DAS",
  "DE",
  "DEL",
  "DER",
  "DI",
  "DIE",
  "DD",
  "EL",
  "LA",
  "LAS",
  "LE",
  "LES",
  "LOS",
  "MAC",
  "MC",
  "VAN",
  "VON",
  "Y",
]);
/** Primeros nombres que se saltan si hay otro (José, María). */
const NOMBRES_COMUNES = new Set(["JOSE", "J", "MARIA", "MA", "M"]);
/** Palabras que no pueden formar las 4 letras; la CURP cambia la 2a por X y el RFC la 4a. */
const PALABRAS_ALTISONANTES = new Set([
  "BACA",
  "BAKA",
  "BUEI",
  "BUEY",
  "CACA",
  "CACO",
  "CAGA",
  "CAGO",
  "CAKA",
  "CAKO",
  "COGE",
  "COGI",
  "COJA",
  "COJE",
  "COJI",
  "COJO",
  "COLA",
  "CULO",
  "FALO",
  "FETO",
  "GETA",
  "GUEI",
  "GUEY",
  "JETA",
  "JOTO",
  "KACA",
  "KACO",
  "KAGA",
  "KAGO",
  "KAKA",
  "KAKO",
  "KOGE",
  "KOGI",
  "KOJA",
  "KOJE",
  "KOJI",
  "KOJO",
  "KOLA",
  "KULO",
  "LILO",
  "LOCA",
  "LOCO",
  "LOKA",
  "LOKO",
  "MAME",
  "MAMO",
  "MEAR",
  "MEAS",
  "MEON",
  "MIAR",
  "MION",
  "MOCO",
  "MOKO",
  "MULA",
  "MULO",
  "NACA",
  "NACO",
  "PEDA",
  "PEDO",
  "PENE",
  "PIPI",
  "PITO",
  "POPO",
  "PUTA",
  "PUTO",
  "QULO",
  "RATA",
  "ROBA",
  "ROBE",
  "ROBO",
  "RUIN",
  "SENO",
  "TETA",
  "VACA",
  "VAGA",
  "VAGO",
  "VAKA",
  "VUEI",
  "VUEY",
  "WUEI",
  "WUEY",
]);
/** Clave de la entidad de nacimiento por su código INEGI (el id del catálogo de estados); otra = nacido en el extranjero. */
const ESTADOS_CURP: Record<number, string> = {
  1: "AS",
  2: "BC",
  3: "BS",
  4: "CC",
  5: "CL",
  6: "CM",
  7: "CS",
  8: "CH",
  9: "DF",
  10: "DG",
  11: "GT",
  12: "GR",
  13: "HG",
  14: "JC",
  15: "MC",
  16: "MN",
  17: "MS",
  18: "NT",
  19: "NL",
  20: "OC",
  21: "PL",
  22: "QT",
  23: "QR",
  24: "SP",
  25: "SL",
  26: "SR",
  27: "TC",
  28: "TS",
  29: "TL",
  30: "VZ",
  31: "YN",
  32: "ZS",
};
const DIGITOS_CURP = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";

/** Palabras en mayúsculas, sin acentos ni partículas; la Ñ cuenta como X. */
const palabrasParaClave = (texto: string) => {
  const palabras = texto
    .toUpperCase()
    .replace(/Ñ/g, "X")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const sinParticulas = palabras.filter((palabra) => !PARTICULAS_NOMBRE.has(palabra));
  return sinParticulas.length ? sinParticulas : palabras;
};
/** Apellido compuesto: cuenta la primera palabra (sin partículas). */
const apellidoParaClave = (apellido: string) => palabrasParaClave(apellido)[0] ?? "";
/** Nombre compuesto: se salta José o María si hay otro nombre. */
const nombreParaClave = (nombres: string) => {
  const palabras = palabrasParaClave(nombres);
  return palabras.length > 1 && NOMBRES_COMUNES.has(palabras[0]) ? palabras[1] : (palabras[0] ?? "");
};
const vocalInterna = (palabra: string) => palabra.slice(1).match(/[AEIOU]/)?.[0] ?? "X";
const consonanteInterna = (palabra: string) => palabra.slice(1).match(/[B-DF-HJ-NP-TV-Z]/)?.[0] ?? "X";

export interface DatosClavePersona {
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  /** "YYYY-MM-DD". */
  fechaNacimiento: string;
}

/**
 * Primeras 10 posiciones del RFC de persona física (SAT): 4 letras del nombre y la fecha AAMMDD. La homoclave la
 * asigna el SAT y no se calcula. Sin apellido materno van 2 letras del paterno y 2 del nombre; con paterno de 1 o 2
 * letras, la inicial de cada apellido y 2 del nombre. `""` si faltan datos.
 */
export const calcularRfcPf = ({ nombres, apellidoPaterno, apellidoMaterno, fechaNacimiento }: DatosClavePersona) => {
  const paterno = apellidoParaClave(apellidoPaterno);
  const materno = apellidoParaClave(apellidoMaterno);
  const nombre = nombreParaClave(nombres);
  const fecha = /^\d{2}(\d{2})-(\d{2})-(\d{2})$/.exec(fechaNacimiento);
  if (!paterno || !nombre || !fecha) return "";

  let letras: string;
  if (!materno) letras = paterno.slice(0, 2) + nombre.slice(0, 2);
  else if (paterno.length <= 2) letras = paterno[0] + materno[0] + nombre.slice(0, 2);
  else letras = paterno[0] + vocalInterna(paterno) + materno[0] + nombre[0];
  if (PALABRAS_ALTISONANTES.has(letras)) letras = letras.slice(0, 3) + "X";

  return letras + fecha[1] + fecha[2] + fecha[3];
};

/**
 * CURP calculada (RENAPO): letras, fecha AAMMDD, sexo (H/M), entidad, consonantes internas, diferenciador por siglo
 * (0 antes de 2000, A desde 2000) y dígito verificador. El diferenciador real lo asigna RENAPO para evitar
 * duplicados, así que es un valor sugerido que el usuario puede corregir. `""` si faltan datos.
 */
export const calcularCurp = ({
  sexo,
  estadoNacimiento,
  ...datos
}: DatosClavePersona & { sexo: string; estadoNacimiento: number | string }) => {
  const paterno = apellidoParaClave(datos.apellidoPaterno);
  const materno = apellidoParaClave(datos.apellidoMaterno);
  const nombre = nombreParaClave(datos.nombres);
  const fecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datos.fechaNacimiento);
  if (!paterno || !nombre || !fecha || !sexo || !estadoNacimiento) return "";

  let letras = paterno[0] + vocalInterna(paterno) + (materno[0] ?? "X") + nombre[0];
  if (PALABRAS_ALTISONANTES.has(letras)) letras = letras[0] + "X" + letras.slice(2);

  const [, anio, mes, dia] = fecha;
  const base =
    letras +
    anio.slice(2) +
    mes +
    dia +
    convertSex(sexo) +
    (ESTADOS_CURP[Number(estadoNacimiento)] ?? "NE") +
    consonanteInterna(paterno) +
    (materno ? consonanteInterna(materno) : "X") +
    consonanteInterna(nombre) +
    (Number(anio) < 2000 ? "0" : "A");
  const suma = [...base].reduce((acc, letra, i) => acc + DIGITOS_CURP.indexOf(letra) * (18 - i), 0);
  return base + ((10 - (suma % 10)) % 10);
};
